// Optional source-file picker for a manual certification. The file is hashed in
// this browser and NEVER uploaded — only the digests are POSTed.
import { Alert, AlertIcon, Box, Button, HStack, Input, Text } from "@chakra-ui/react";
import { isUnrecognisedWorkType, producesLedgerEntry } from "../provenance/workType";

interface Props {
  value: File | null;
  onChange: (file: File | null) => void;
  workType: string;
  isDisabled: boolean;
  /** False on the Add Work lane when no certification type has been chosen.
   *  The backend only writes provenance for an APPROVED certification, and Add
   *  Work creates a PENDING one when no type is picked — so a file attached in
   *  that state is hashed, POSTed and dropped. Gate the control instead of
   *  letting the agent believe they attested something. */
  certificationSelected?: boolean;
}

export default function SourceFileField({
  value,
  onChange,
  workType,
  isDisabled,
  certificationSelected = true,
}: Props) {
  // NOT `workType === "OTHER"`: Add Work passes raw free text from a datalist
  // input, WorkDetail passes the canonical enum. Both go through the same
  // normalisation the server applies before it decides whether the work type
  // maps to a coc MediaType at all.
  const typeBlocks = !producesLedgerEntry(workType);
  const typeUnrecognised = isUnrecognisedWorkType(workType);
  // buildProvenance() returns null ONLY for a zero-byte file (buildProvenance.ts).
  // A File object with size 0 is therefore exactly as ledger-inert as no file at
  // all — warn on that condition, not merely on whether a File is selected.
  const producesNoProvenance = !value || value.size === 0;
  const blocked = isDisabled || !certificationSelected;
  return (
    <Box>
      <Text fontSize="sm" mb={1}>
        Source file (optional)
      </Text>
      <Text fontSize="xs" color="gray.600" mb={2}>
        Hashed in your browser to produce the chain-of-creation record. The file itself is
        never uploaded.
      </Text>
      <Input
        type="file"
        p={1}
        isDisabled={blocked}
        onChange={(e) => onChange(e.target.files?.[0] ?? null)}
      />
      {value && (
        <HStack mt={2} spacing={3}>
          <Text fontSize="sm">{value.name}</Text>
          <Button size="xs" variant="ghost" isDisabled={blocked} onClick={() => onChange(null)}>
            Remove
          </Button>
        </HStack>
      )}
      {!certificationSelected ? (
        <Alert status="warning" mt={2} fontSize="sm" borderRadius={6}>
          <AlertIcon />
          Choose a certification type first — a source file is only recorded for an approved
          certification, so it would be discarded.
        </Alert>
      ) : typeBlocks ? (
        <Alert status="warning" mt={2} fontSize="sm" borderRadius={6}>
          <AlertIcon />
          {typeUnrecognised ? (
            <>
              Work type “{workType}” is not a recognised type and will be stored as “Other”,
              which never produces a ledger entry, with or without a file.
            </>
          ) : (
            <>Work type “Other” never produces a ledger entry, with or without a file.</>
          )}
        </Alert>
      ) : (
        producesNoProvenance && (
          <Alert status="info" mt={2} fontSize="sm" borderRadius={6}>
            <AlertIcon />
            {value ? (
              <>
                Selected file is empty (0 bytes) — this certification will not produce a
                chain-of-creation ledger entry.
              </>
            ) : (
              <>
                No file attached — this certification will not produce a chain-of-creation ledger
                entry.
              </>
            )}
          </Alert>
        )
      )}
    </Box>
  );
}
